class CollectionShowcase extends HTMLElement {
  connectedCallback() {
    queueMicrotask(() => { if (this.isConnected && !this.controller) this.initialize(); });
  }

  initialize() {
    this.rail = this.querySelector('[data-rail]');
    this.cards = [...this.querySelectorAll('[data-collection-card]')];
    this.controls = this.querySelector('[data-controls]');
    if (!this.rail || !this.cards.length) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.motion = matchMedia('(prefers-reduced-motion: reduce)');
    this.pointer = null;
    this.dragged = false;
    this.mediaNearby = !('IntersectionObserver' in window);
    this.visibleCards = new Set();
    if (!this.mediaNearby) {
      this.mediaObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) this.visibleCards.add(entry.target);
          else this.visibleCards.delete(entry.target);
        });
        this.mediaNearby = this.visibleCards.size > 0;
        this.scheduleMedia();
      }, { rootMargin: '240px 0px' });
      this.cards.forEach(card => this.mediaObserver.observe(card));
    }
    this.rail.addEventListener('load', () => this.scheduleMedia(), { signal, capture: true });
    this.rail.addEventListener('error', () => this.scheduleMedia(), { signal, capture: true });
    this.rail.addEventListener('scroll', () => this.measure(), { signal, passive: true });
    this.rail.addEventListener('pointerdown', event => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, scroll: this.rail.scrollLeft, type: event.pointerType };
      this.dragged = false;
    }, { signal, passive: true });
    this.rail.addEventListener('dragstart', event => event.preventDefault(), { signal });
    window.addEventListener('pointermove', event => {
      if (!this.pointer || event.pointerId !== this.pointer.id) return;
      const dx = event.clientX - this.pointer.x;
      const dy = event.clientY - this.pointer.y;
      if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) this.dragged = true;
      if (this.dragged && this.pointer.type === 'mouse') {
        event.preventDefault();
        this.rail.scrollLeft = this.pointer.scroll - dx;
      }
    }, { signal, passive: false });
    window.addEventListener('pointerup', () => { this.pointer = null; }, { signal });
    window.addEventListener('pointercancel', () => { this.pointer = null; this.dragged = false; }, { signal });
    this.rail.addEventListener('click', event => {
      if (this.dragged && event.detail > 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) event.preventDefault();
      this.dragged = false;
    }, { signal, capture: true });
    this.addEventListener('focusin', event => {
      // Pointer focus must not move a partially visible link before its click.
      if (!this.pointer && event.target.matches(':focus-visible')) this.reveal(event.target.closest('[data-collection-card]'));
    }, { signal });
    this.controls?.querySelector('[data-previous]').addEventListener('click', () => this.move(-1), { signal });
    this.controls?.querySelector('[data-next]').addEventListener('click', () => this.move(1), { signal });
    this.addEventListener('shopify:block:select', event => this.reveal(event.target.closest('[data-collection-card]')), { signal });
    this.resize = new ResizeObserver(() => this.measure());
    this.resize.observe(this.rail);
    this.cards.forEach(card => this.resize.observe(card));
    this.measure();
  }

  measure() {
    const limit = this.rail.scrollWidth - this.rail.clientWidth;
    const offset = Math.abs(this.rail.scrollLeft);
    this.rail.toggleAttribute('data-can-scroll-start', offset > 2);
    this.rail.toggleAttribute('data-can-scroll-end', offset < limit - 2);
    if (!this.mediaObserver) this.scheduleMedia();
    if (!this.controls) return;
    this.controls.hidden = limit <= 2;
    this.controls.querySelector('[data-previous]').disabled = offset <= 2;
    this.controls.querySelector('[data-next]').disabled = offset >= limit - 2;
  }

  scheduleMedia() {
    if (!this.controller || !this.isConnected || !this.mediaNearby || this.mediaFrame) return;
    this.mediaFrame = requestAnimationFrame(() => {
      this.mediaFrame = null;
      if (this.mediaNearby) this.loadVisibleMedia();
    });
  }

  prioritizeMedia(card, priority = 'auto') {
    const images = [...card.querySelectorAll('img')];
    images.forEach(image => {
      image.loading = 'eager';
      if (image.fetchPriority !== 'high') image.fetchPriority = priority;
    });
    return images;
  }

  loadVisibleMedia() {
    let visible;
    if (this.mediaObserver) visible = this.cards.filter(card => this.visibleCards.has(card));
    else {
      const bounds = this.rail.getBoundingClientRect();
      visible = this.cards.filter(card => {
        const rect = card.getBoundingClientRect();
        return rect.right > bounds.left && rect.left < bounds.right;
      });
    }
    const images = visible.flatMap(card => this.prioritizeMedia(card));
    const connection = navigator.connection;
    if (!visible.length || images.some(image => !image.complete) || connection?.saveData || ['slow-2g', '2g'].includes(connection?.effectiveType)) return;
    const next = this.cards[this.cards.indexOf(visible.at(-1)) + 1];
    if (next) this.prioritizeMedia(next, 'low');
  }

  reveal(card) {
    if (!card) return;
    this.prioritizeMedia(card);
    const rail = this.rail.getBoundingClientRect();
    const target = card.getBoundingClientRect();
    if (target.left < rail.left) this.rail.scrollBy({ left: target.left - rail.left - 16, behavior: 'instant' });
    else if (target.right > rail.right) this.rail.scrollBy({ left: target.right - rail.right + 16, behavior: 'instant' });
  }

  move(direction) {
    const stride = this.cards[0].getBoundingClientRect().width + (parseFloat(getComputedStyle(this.cards[0].parentElement).columnGap) || 0);
    const rtl = getComputedStyle(this.rail).direction === 'rtl' ? -1 : 1;
    this.rail.scrollBy({ left: direction * rtl * stride, behavior: this.motion.matches ? 'instant' : 'smooth' });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    this.resize?.disconnect();
    this.mediaObserver?.disconnect();
    this.mediaObserver = null;
    this.visibleCards?.clear();
    cancelAnimationFrame(this.mediaFrame);
    this.mediaFrame = null;
    this.mediaNearby = false;
    this.pointer = null;
    if (this.controls) this.controls.hidden = true;
  }
}

if (!customElements.get('collection-showcase')) customElements.define('collection-showcase', CollectionShowcase);
